/**
 * IP Geolocation utility for detecting user location
 * Uses ipapi.co free service (1000 requests/day)
 * Implements caching to avoid unnecessary API calls
 */

import { ipLocationCache } from '@/utils/ipLocationCache';

export interface IPLocationData {
  city: string;
  region: string;
  country: string;
  postal: string;
  latitude: number;
  longitude: number;
  timezone: string;
  asn?: string;
  org?: string;
}

export interface IPGeolocationResult {
  success: boolean;
  data?: IPLocationData;
  error?: string;
  isVPN?: boolean;
  cached?: boolean;
}

/**
 * Fetch IP location from primary API (ipapi.co)
 */
const fetchIPLocationFromPrimaryAPI = async (): Promise<IPGeolocationResult> => {
  const response = await fetch('https://ipapi.co/json/', {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Check for error response from ipapi.co
  if (data.error) {
    throw new Error(data.reason || 'IP geolocation service error');
  }

  // Validate required fields
  if (!data.city || !data.region || !data.country) {
    throw new Error('Incomplete location data received');
  }

  const locationData: IPLocationData = {
    city: data.city,
    region: data.region,
    country: data.country_name || data.country,
    postal: data.postal || '',
    latitude: parseFloat(data.latitude) || 0,
    longitude: parseFloat(data.longitude) || 0,
    timezone: data.timezone || '',
    asn: data.asn,
    org: data.org,
  };

  return {
    success: true,
    data: locationData,
    isVPN: data.org?.toLowerCase().includes('vpn') || false,
  };
};

/**
 * Fallback IP geolocation using ip-api.com
 */
const getIPLocationFallback = async (): Promise<IPGeolocationResult> => {
  const response = await fetch('http://ip-api.com/json/', {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Fallback service error! status: ${response.status}`);
  }

  const data = await response.json();

  if (data.status !== 'success') {
    throw new Error(data.message || 'Fallback geolocation service error');
  }

  const locationData: IPLocationData = {
    city: data.city,
    region: data.regionName,
    country: data.country,
    postal: data.zip || '',
    latitude: data.lat || 0,
    longitude: data.lon || 0,
    timezone: data.timezone || '',
    asn: data.as,
    org: data.org,
  };

  return {
    success: true,
    data: locationData,
    isVPN: data.org?.toLowerCase().includes('vpn') || false,
  };
};

/**
 * Get user's location based on their IP address
 * Uses caching to avoid unnecessary API calls
 * Falls back to alternative service if primary fails
 */
export const getIPLocation = async (options?: {
  bypassCache?: boolean;
}): Promise<IPGeolocationResult> => {
  const { bypassCache = false } = options || {};

  // Check cache first (unless bypassed)
  if (!bypassCache) {
    const cached = await ipLocationCache.getCached();
    if (cached) {
      return {
        ...cached,
        // Add cache indicator
        cached: true,
      };
    }
  }

  // Fetch fresh data
  let result: IPGeolocationResult;
  
  try {
    result = await fetchIPLocationFromPrimaryAPI();
  } catch (primaryError) {
    // Fallback to alternative service
    try {
      result = await getIPLocationFallback();
    } catch (fallbackError) {
      result = {
        success: false,
        error: `Failed to get location: ${primaryError instanceof Error ? primaryError.message : 'Unknown error'}`,
      };
    }
  }

  // Cache successful results
  if (result.success) {
    await ipLocationCache.setCached(result);
  }

  return result;
};

/**
 * Format location data into a human-readable address string
 */
export const formatLocationAsAddress = (location: IPLocationData): string => {
  const parts = [];
  
  if (location.city) parts.push(location.city);
  if (location.region) parts.push(location.region);
  if (location.postal) parts.push(location.postal);
  if (location.country) parts.push(location.country);
  
  return parts.join(', ');
};

/**
 * Format location data for geocoding services
 * Optimized for better geocoding success rates
 */
export const formatLocationForGeocoding = (location: IPLocationData): string => {
  // For geocoding, we want the most specific location first
  const parts = [];
  
  // Start with city and state for better geocoding
  if (location.city && location.region) {
    parts.push(`${location.city}, ${location.region}`);
  } else if (location.city) {
    parts.push(location.city);
  } else if (location.region) {
    parts.push(location.region);
  }
  
  // Add postal code if available for precision
  if (location.postal) {
    parts.push(location.postal);
  }
  
  // Always include country for context
  if (location.country) {
    parts.push(location.country);
  }
  
  return parts.join(', ');
};

/**
 * Check if the detected location seems accurate
 * Returns false for common VPN/proxy indicators
 */
export const isLocationAccurate = (result: IPGeolocationResult): boolean => {
  if (!result.success || !result.data) return false;
  
  // Check for VPN indicators
  if (result.isVPN) return false;
  
  // Check for generic/suspicious city names
  const suspiciousCities = ['Unknown', 'Private', 'Reserved', 'Anonymous'];
  if (suspiciousCities.includes(result.data.city)) return false;
  
  // Check for zero coordinates (invalid)
  if (result.data.latitude === 0 && result.data.longitude === 0) return false;
  
  return true;
};

/**
 * Get a confidence score for the location (0-100)
 */
export const getLocationConfidence = (result: IPGeolocationResult): number => {
  if (!result.success || !result.data) return 0;
  
  let confidence = 50; // Base confidence
  
  // Increase confidence for complete data
  if (result.data.city) confidence += 15;
  if (result.data.region) confidence += 15;
  if (result.data.postal) confidence += 10;
  if (result.data.latitude && result.data.longitude) confidence += 10;
  
  // Decrease confidence for VPN/proxy
  if (result.isVPN) confidence -= 30;
  
  // Decrease confidence for suspicious patterns
  if (!isLocationAccurate(result)) confidence -= 20;
  
  // Slightly increase confidence for cached results (they were previously validated)
  if (result.cached) confidence += 5;
  
  return Math.max(0, Math.min(100, confidence));
};

/**
 * Clear the IP location cache
 */
export const clearIPLocationCache = (): void => {
  ipLocationCache.clearCache();
};

/**
 * Get cache statistics
 */
export const getIPLocationCacheStats = async () => ipLocationCache.getCacheStats();

/**
 * Check if caching is available
 */
export const isIPLocationCacheAvailable = (): boolean => ipLocationCache.isCacheAvailable();

/**
 * Force refresh location data (bypass cache)
 */
export const refreshIPLocation = async (): Promise<IPGeolocationResult> => 
  getIPLocation({ bypassCache: true });